// Every call to one of our servlets goes through apiFetch. The servlets answer only
// a session started by the site (filters/RequestGuard.java): the first call asks
// /startsession for one, which sets the session cookie and returns a token that each
// call sends back in the X-TMS-Token header. A session that has ended (an hour idle,
// or a Tomcat restart) answers 401 with X-TMS-Session: required, and the call starts
// a new one and is sent once more.
//
// Static files (the Big Picture's exported maps, the basemap) don't need it: fetch
// them directly. Keep this file free of other imports (but the small track.js), as the
// landing page uses it.
//
// The first /startsession also says where the page was opened from (track.js), for the
// admin portal's Traffic page.
import { baseUrl } from './Layers/layerBasics';
import { sessionBody, track } from './track';

const TOKEN_HEADER = 'X-TMS-Token';

let sessionPromise = null;

const startSession = () => {
    if (!sessionPromise) {
        sessionPromise = fetch(baseUrl + '/startsession', {
            method:'POST',
            credentials:'include',
            headers:{'ngrok-skip-browser-warning':'true', 'Content-Type':'application/json'},
            body:JSON.stringify(sessionBody()),
        })
            .then((res) => res.ok ? res.json() : Promise.reject(new Error('startsession ' + res.status)))
            .then((json) => {
                if (!json || !json.token) throw new Error('startsession: no token');
                return json.token;
            })
            .catch((e) => {
                // Asked again on the next call
                sessionPromise = null;
                throw e;
            });
    }
    return sessionPromise;
};

// The window event the map listens for (account/MapGate.js) when the server refuses a
// guest more places or layers (util/GuestLimits.java); its detail is 'search' or 'layers'
export const GUEST_LIMIT_EVENT = 'tms-guest-limit';

export const isGuestLimit = (res) => res.status === 403 && !!res.headers.get('X-TMS-Guest-Limit');

// The window event the map listens for (account/MapGate.js) when the server refuses a price or
// crime graph (util/GraphAccess.java: subscribers, or a bought report's own area); its detail
// is the refused function, as street_crime_timeseries
export const GRAPH_LOCKED_EVENT = 'tms-graph-locked';

export const isGraphLocked = (res) => res.status === 403 && !!res.headers.get('X-TMS-Graph-Locked');

const sessionEnded = (res) => res.status === 401 && res.headers.get('X-TMS-Session') === 'required';

// fetch for a servlet: path is the servlet's path (as '/propserve') or a full URL
// under baseUrl. options are fetch's; the token and ngrok headers are added, and
// credentials default to 'include'. The body must be one that can be sent twice
// (a string, URLSearchParams or FormData), as it is on a retry.
export async function apiFetch(path, options = {}) {
    const url = path.startsWith('/') && !path.startsWith(baseUrl + '/') ? baseUrl + path : path;
    const send = (token) => fetch(url, {
        method:'POST',
        credentials:'include',
        ...options,
        headers:{...(options.headers || {}), 'ngrok-skip-browser-warning':'true', [TOKEN_HEADER]:token},
    });
    let token = await startSession();
    let res = await send(token);
    if (sessionEnded(res)) {
        if (sessionPromise && (await sessionPromise.catch(() => null)) === token) {
            sessionPromise = null;
        }
        token = await startSession();
        res = await send(token);
    }
    if (isGuestLimit(res) && typeof window !== 'undefined') {
        track('limit', res.headers.get('X-TMS-Guest-Limit'));
        window.dispatchEvent(new CustomEvent(GUEST_LIMIT_EVENT, {detail:res.headers.get('X-TMS-Guest-Limit')}));
    }
    if (isGraphLocked(res) && typeof window !== 'undefined') {
        track('graph_locked', res.headers.get('X-TMS-Graph-Locked'));
        window.dispatchEvent(new CustomEvent(GRAPH_LOCKED_EVENT, {detail:res.headers.get('X-TMS-Graph-Locked')}));
    }
    return res;
}

// POST params (an object) form encoded, as the map data servlets read them
export const apiPostForm = (path, params, options = {}) =>
    apiFetch(path, {...options, body:new URLSearchParams(params)});
